import React from "react";
import { CornerDownLeft } from "lucide-react";
import { formatMoney } from "./rateCardConstants";
import {
  DEPTS,
  DEPT_LABELS,
  actualDeptPerUnit,
  actualHoursPerUnit,
  classifyCushion,
  cushionEvidence,
  cushionEvidenceFlags,
  cushionGap,
  cushionRatio,
  detailQuantity,
  formatCushion,
  formatHours,
  lineQuantity,
  qtySource,
  quotedDeptPerUnit,
  quotedHoursPerUnit,
  sharedAcrossLines,
  shiftWindow,
  toNumber,
} from "./costedJobReference";
import "./CostedJobReferences.css";

const VERDICT_TITLES = {
  good: "The quote covered the work",
  tight: "Close to break-even — little cushion either way",
  under: "We under-quoted this labor",
};

/* When the actual hours behind the ratio were inferred rather than measured,
   the headline stops being a verdict and becomes an estimate — said plainly,
   and drained of the green/red that would otherwise settle the question. */
const SOFT_VERDICT_TITLES = {
  "unverified-qty": "Approximate — the unit count was guessed",
  shared: "Approximate — a job-level figure, not a per-line one",
};

/**
 * The inspector half of the Costed Job References screen: one indexed line in
 * full. Quoted hours from the costing sheet, actual hours clocked in Island
 * Time, the gap between them department by department, and the shift window
 * and source sheet the numbers came from.
 *
 * The single action is "Pull rates", which hands the line to the Rate Card
 * Builder's existing pull-rates flow — this pane computes no rate itself.
 */
function CostedJobReferenceDetail({
  item,
  scope,
  onPullRates,
  canPullRates = true,
}) {
  if (!item) {
    return (
      <div className="cjr-detail" data-scope={scope}>
        <p className="cjr-detail__empty">
          Pick a costed line on the left to see what it was quoted at, what the
          shop actually spent, and where the numbers came from.
        </p>
      </div>
    );
  }

  const quotedTotal = quotedHoursPerUnit(item);
  const actualTotal = actualHoursPerUnit(item);
  const ratio = cushionRatio(item);
  const tone = classifyCushion(ratio);
  const gap = cushionGap(item);
  const evidence = cushionEvidence(item);
  const evidenceFlags = cushionEvidenceFlags(item);
  const softened = tone !== null && evidenceFlags.length > 0;
  const detail = item.actual_labor_detail || null;
  const quotedDept = quotedDeptPerUnit(item);
  const actualDept = actualDeptPerUnit(detail);
  const hasQuotedSplit = DEPTS.some((d) => quotedDept[d] !== null);
  const hasActualSplit = DEPTS.some((d) => actualDept[d] !== null);
  const shiftRange = shiftWindow(detail);
  const detailQty = detailQuantity(detail);
  const shopTotal = toNumber(detail && detail.shop_total_hours);
  const qty = lineQuantity(item);

  const dims = item.description || item.dimensions_raw;

  return (
    <div className="cjr-detail" data-scope={scope}>
      <p className="cjr-detail__eyebrow">Costed job reference</p>
      <h3 className="cjr-detail__title">{item.name}</h3>
      {dims && <div className="cjr-detail__dims">{dims}</div>}
      <div className="cjr-detail__job">
        {item.material_type && (
          <span className="cjr__mat">{item.material_type}</span>
        )}
        <span>
          {[
            item.job_number,
            `${qty} unit${qty === 1 ? "" : "s"}`,
            item.source_file,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>

      {/* The verdict — the one thing the owner is here to read. */}
      <div
        className={`cjr-detail__verdict cjr-detail__verdict--${
          softened ? "soft" : tone || "none"
        }`}
      >
        <span className="cjr-detail__verdict-n">
          {tone ? `${softened ? "~" : ""}${formatCushion(ratio)}` : "n/a"}
        </span>
        <div>
          <div className="cjr-detail__verdict-t">
            {softened
              ? SOFT_VERDICT_TITLES[evidence]
              : tone
                ? VERDICT_TITLES[tone]
                : "No actual hours to compare"}
          </div>
          <div className="cjr-detail__verdict-d">
            {tone
              ? `${softened ? "On these numbers, t" : "T"}${
                  gap >= 0
                    ? `he sheet allowed ${formatHours(gap)} hrs per unit more than the shop spent.`
                    : `he shop spent ${formatHours(Math.abs(gap))} hrs per unit more than the sheet allowed.`
                }${softened ? " Read the Island Time note below before trusting it." : ""}`
              : "No Island Time shop hours are attached to this line, so there is nothing to compare the quote against."}
          </div>
        </div>
      </div>

      <div className="cjr-detail__card">
        <div className="cjr-detail__hd">
          <span>Labor by department · hr per unit</span>
        </div>
        <table className="cjr-detail__dt">
          <thead>
            <tr>
              <th>Department</th>
              <th className="cjr-detail__d-quoted">Quoted</th>
              <th className="cjr-detail__d-actual">Actual</th>
              <th className="cjr-detail__d-delta">Δ hr</th>
            </tr>
          </thead>
          <tbody>
            {DEPTS.map((dept) => (
              <tr key={dept}>
                <td>{DEPT_LABELS[dept]}</td>
                <td className="cjr-detail__d-quoted">
                  <HoursCell
                    value={quotedDept[dept]}
                    className="cjr-detail__q"
                  />
                </td>
                <td className="cjr-detail__d-actual">
                  <HoursCell
                    value={actualDept[dept]}
                    className="cjr-detail__a"
                  />
                </td>
                <td className="cjr-detail__d-delta">
                  <DeltaCell
                    quoted={quotedDept[dept]}
                    actual={actualDept[dept]}
                  />
                </td>
              </tr>
            ))}
            <tr className="is-total">
              <td>Per unit</td>
              <td className="cjr-detail__d-quoted">
                <HoursCell value={quotedTotal} className="cjr-detail__q" />
              </td>
              <td className="cjr-detail__d-actual">
                <HoursCell value={actualTotal} className="cjr-detail__a" />
              </td>
              <td className="cjr-detail__d-delta">
                <DeltaCell quoted={quotedTotal} actual={actualTotal} />
              </td>
            </tr>
          </tbody>
        </table>
        {!hasQuotedSplit && (
          <p className="cjr-detail__empty">
            The sheet recorded a line total only — no department split.
          </p>
        )}
        {actualTotal !== null && !hasActualSplit && (
          <p className="cjr-detail__empty">
            Island Time gave a total for this line but no department breakdown.
          </p>
        )}
      </div>

      <div className="cjr-detail__card">
        <div className="cjr-detail__hd">
          <span>
            {actualTotal !== null
              ? "Island Time · clocked hours"
              : "Island Time"}
          </span>
        </div>
        {actualTotal !== null ? (
          <div className="cjr-detail__rows">
            <DetailRow
              k="Total clocked"
              v={
                shopTotal !== null && detailQty
                  ? `${formatHours(shopTotal)} hrs across ${detailQty} unit${detailQty === 1 ? "" : "s"}`
                  : `${formatHours(actualTotal)} hrs per unit`
              }
            />
            {shiftRange && <DetailRow k="Shift window" v={shiftRange} />}
            <DetailRow
              k="Source"
              v={
                item.actual_labor_source === "island_time"
                  ? "Island Time"
                  : item.actual_labor_source || "—"
              }
            />
            {detail?.category && (
              <DetailRow k="Matched as" v={String(detail.category)} />
            )}
            {detail?.job_scope && (
              <DetailRow k="Job scope" v={String(detail.job_scope)} />
            )}
            {/* The caveat sits with the number it qualifies, not in a legend
                somewhere else. Divide by a guessed unit count and the per-unit
                figure inherits the guess. */}
            {/* Both caveats can be true at once, and each is rendered on its
                own terms — one is not allowed to hide the other. */}
            {evidenceFlags.includes("unverified-qty") && (
              <p className="cjr-detail__warn">
                Quantity was guessed from the job description (source:{" "}
                {qtySource(item)}) — the per-unit figure may be off by the true
                unit count.
              </p>
            )}
            {evidenceFlags.includes("shared") && (
              <p className="cjr-detail__warn">
                Job-level figure shared across {sharedAcrossLines(item)} lines —
                not measured per line.
              </p>
            )}
          </div>
        ) : (
          <p className="cjr-detail__empty">
            No clocked shop hours are attached to this line — either the job ran
            before Island Time tracked shop hours, or no task matched it.
            Pulling rates from it uses the quoted hours alone.
          </p>
        )}
      </div>

      <div className="cjr-detail__card">
        <div className="cjr-detail__hd">
          <span>On the costing sheet</span>
        </div>
        <div className="cjr-detail__rows">
          <DetailRow k="Quantity" v={item.quantity ?? "—"} />
          <DetailRow k="Unit cost" v={formatMoney(item.unit_cost)} />
          <DetailRow k="Line total" v={formatMoney(item.total_cost)} />
          <DetailRow k="Sheet" v={item.source_file || "—"} />
        </div>
      </div>

      {canPullRates ? (
        <>
          <div className="cjr-detail__cta">
            <button
              type="button"
              className="wb-plain cjr-detail__ctabtn"
              onClick={() => onPullRates(item)}
            >
              <CornerDownLeft aria-hidden="true" />
              Pull rates
            </button>
          </div>
          {/* Which hours the pull will use is decided by the scope switcher
              above the table, so the hint has to say which well it draws
              from — otherwise a card built off actuals looks identical to one
              built off the old quote. */}
          <p className="cjr-detail__ctahint">
            {scope === "act"
              ? actualTotal !== null
                ? "Fills the card with this line's size, quantity and the shop hours clocked in Island Time. Every number stays editable."
                : "No Island Time hours on this line — the pull will fall back to the quoted hours. Every number stays editable."
              : "Fills the card with this line's size, quantity and quoted hours. Every number stays editable."}
          </p>
        </>
      ) : (
        /* A banded family has one set of rates per band, so there is no single
           card to fill. The comparison above is still the point of being here. */
        <p className="cjr-detail__ctahint">
          Pulling rates fills a single-rate card. Switch this card off size
          bands to pull from this line, or read the hours above and type the
          band you are tuning.
        </p>
      )}
    </div>
  );
}

function HoursCell({ value, className }) {
  if (value === null || value === undefined) {
    return <span className="cjr-detail__d-na">—</span>;
  }
  return <span className={className}>{formatHours(value)}</span>;
}

function DeltaCell({ quoted, actual }) {
  if (
    quoted === null ||
    quoted === undefined ||
    actual === null ||
    actual === undefined
  ) {
    return <span className="cjr-detail__d-na">—</span>;
  }
  const diff = quoted - actual;
  const positive = diff >= 0;
  return (
    <span
      className={positive ? "cjr-detail__d-pos" : "cjr-detail__d-neg"}
    >{`${positive ? "+" : "−"}${formatHours(Math.abs(diff))}`}</span>
  );
}

function DetailRow({ k, v }) {
  return (
    <div className="cjr-detail__row">
      <span className="cjr-detail__k">{k}</span>
      <span className="cjr-detail__v">{v}</span>
    </div>
  );
}

export default CostedJobReferenceDetail;
